import { useEffect, useState } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { Feed } from '@pushly/models/lib/structs/feeds/feed'
import { FeedService, IFeedSelectOption } from '../../../../services/feed.service'
import { isFeedSelected } from './helpers'

/**
 * Request cancellation keys — one per effect, following the journey convention of
 * a dot-scoped name (see `use-variant-metrics.ts`'s `split-test-results.*`).
 *
 * Deliberately STABLE (no domain/feed id in the key): `axiosFetch` cancels the
 * previous in-flight request registered under the same key, so reusing one key
 * per effect is what actually aborts a superseded request. Keying by feed id
 * would give every selection its own key and abort nothing.
 */
const FEED_OPTIONS_CANCELLATION_KEY = 'journey-feed-selection.feed-options'
const FEED_DETAIL_CANCELLATION_KEY = 'journey-feed-selection.feed-detail'

/** State + loaders returned by {@link useFeedSelection}. */
export interface FeedSelectionState {
    selectedFeedId: number | undefined
    setSelectedFeedId: (feedId: number | undefined) => void
    /** Trimmed `{ id, name, type }` options for the feed picker. */
    feedOptions: IFeedSelectOption[]
    loadingFeeds: boolean
    feedError: boolean
    /** Full feed for the current selection (url + nested config), lazily fetched. */
    loadedFeed: Feed | undefined
    loadingFeed: boolean
    feedDetailError: boolean
}

/**
 * Encapsulates the domain feed-option list and the on-demand full-feed detail
 * fetch used by the unified content-source editor's feed source (the "Source"
 * well picker plus the read-only Feed Setup tab).
 *
 * Loading/error handling mirrors the domain-scoped option loaders used by other
 * journey editors: the service promises never reject — we branch on `ok` /
 * `cancelled` and surface an error flag otherwise. Both effects use a
 * cancelled-flag race guard so a stale response can't clobber a newer selection,
 * pass a cancellation key so the superseded request is actually aborted, and
 * clear their loading flag on cleanup so a superseded/disabled run can never
 * leave a spinner running forever.
 *
 * @param domainId   The active domain id.
 * @param initialFeedId  The feed already selected on the step (if any).
 * @param opts.enabled   When false, no network calls are made (used by the
 *   unified editor so feeds are only fetched once the Feed source is selected).
 *   Defaults to true. Hooks are always invoked regardless — only the fetches
 *   are gated — so the Rules of Hooks are preserved.
 */
export function useFeedSelection(
    domainId: number,
    initialFeedId: number | undefined,
    opts?: { enabled?: boolean },
): FeedSelectionState {
    const enabled = opts?.enabled ?? true
    const feedSvc = useService(FeedService)

    const [loadingFeeds, setLoadingFeeds] = useState(false)
    const [feedError, setFeedError] = useState(false)
    const [feedOptions, setFeedOptions] = useState<IFeedSelectOption[]>([])
    const [selectedFeedId, setSelectedFeedId] = useState<number | undefined>(initialFeedId)

    const [loadingFeed, setLoadingFeed] = useState(false)
    const [feedDetailError, setFeedDetailError] = useState(false)
    const [loadedFeed, setLoadedFeed] = useState<Feed | undefined>()

    // Load the domain's feeds for selection. Deferred until `enabled` so the
    // unified editor doesn't fetch feeds for a BlueConic/Vespa step; when the
    // user switches to the Feed source, `enabled` flips and this runs.
    useEffect(() => {
        if (!enabled) {
            setLoadingFeeds(false)
            return
        }

        let cancelled = false

        const loadFeeds = async () => {
            setLoadingFeeds(true)
            setFeedError(false)

            const res = await feedSvc.fetchDomainFeedOptions(domainId, {
                cancellationKey: FEED_OPTIONS_CANCELLATION_KEY,
            })

            // Stale response: a newer run (or a disable/unmount) owns the state now.
            // Loading is reset by this run's cleanup instead of here — clearing it
            // from a stale resolution would blank the spinner for the request the
            // newer run has in flight.
            if (cancelled) return

            if (res.ok) {
                setFeedOptions(res.data ?? [])
            } else if (!res.cancelled) {
                setFeedError(true)
            }

            setLoadingFeeds(false)
        }

        loadFeeds()

        return () => {
            cancelled = true
            // Never leave the spinner on for a superseded/disabled/unmounted run —
            // its resolution bails at the guard above without clearing it. A
            // subsequent run re-arms loading in its own body.
            setLoadingFeeds(false)
        }
    }, [enabled, domainId])

    // Fetch the full feed whenever the selection changes (and on mount when a
    // feed is preselected) so the Feed Configuration card can show url +
    // constraints. fetchFeedById never rejects; a race guard ignores a stale
    // response if the selection changed while the fetch was in flight.
    useEffect(() => {
        if (!enabled) {
            setLoadingFeed(false)
            return
        }

        if (!isFeedSelected(selectedFeedId)) {
            setLoadedFeed(undefined)
            setFeedDetailError(false)
            setLoadingFeed(false)
            return
        }

        let cancelled = false

        const loadFeed = async () => {
            setLoadingFeed(true)
            setFeedDetailError(false)

            // `fetchFeedById` reports `cancelled` alongside `ok`, so an aborted request
            // is never surfaced as a load failure — same branching as the feed-options
            // effect above.
            const res = await feedSvc.fetchFeedById(domainId, selectedFeedId, {
                cancellationKey: FEED_DETAIL_CANCELLATION_KEY,
            })

            // Stale response — see the note in the feed-options effect above.
            if (cancelled) return

            if (res.ok) {
                setLoadedFeed(Feed.from(res.data))
            } else if (!res.cancelled) {
                setLoadedFeed(undefined)
                setFeedDetailError(true)
            }

            setLoadingFeed(false)
        }

        loadFeed()

        return () => {
            cancelled = true
            setLoadingFeed(false)
        }
    }, [enabled, selectedFeedId, domainId])

    return {
        selectedFeedId,
        setSelectedFeedId,
        feedOptions,
        loadingFeeds,
        feedError,
        loadedFeed,
        loadingFeed,
        feedDetailError,
    }
}
